import { runInAction, when } from 'mobx';
import { LoadingState } from '../types/loading-state';
import { createFetchedData } from './fetched-data/create-fetched-data';
import { getFetchedDataPayload } from './fetched-data/get-fetched-data-payload';
import { isFetchedDataValid } from './fetched-data/is-fetched-data-valid';
import { updateFetchedState } from './fetched-data/update-fetched-state';
/**
 * Loads data from a provided API method and stores it into the provided FetchedData state via the 3 LoadingStates: Loading, Success, and Failure.
 * Will clear any expired cached data and will cache the new data for a given cache duration.
 *
 * @param request The load request parameters.
 * @typeparam R The API response type.
 * @typeparam T The state type.
 * @return Promise<T> The data loaded into the state.
 */
export const loadDataFromApi = async (request) => {
    const { state, cacheDuration, reloadCachedData } = request;
    // Clear any existing data to force a reload.
    if (reloadCachedData)
        updateFetchedState(state, createFetchedData(LoadingState.NotLoaded));
    // Data is still valid, no need to reload yet.
    else if (isFetchedDataValid(state))
        return getFetchedDataPayload(state);
    // Data is already being loaded, wait for it to load, then return data from the first API call.
    else if (state.loadingState === LoadingState.Loading)
        return getNewStateWhenLoaded(state);
    updateFetchedState(state, createFetchedData(LoadingState.Loading));
    try {
        const newState = await getNewState(request);
        updateFetchedState(state, createFetchedData(newState, cacheDuration));
        return newState;
    }
    catch (error) {
        updateFetchedState(state, createFetchedData(LoadingState.Failure));
        throw error;
    }
};
/**
 * Gets the new state for a LoadRequest.
 * Maps the response type to the state type using onSuccess, if provided.
 *
 * The onSuccess function is wrapped with runInAction, in case any other state manipulation is performed.
 *
 * @typeparam R The API response type.
 * @typeparam T The state type.
 */
const getNewState = async (request) => {
    const { api, onSuccess, cacheDuration } = request;
    const response = await api();
    return onSuccess ? runInAction(() => onSuccess(response, cacheDuration)) : response;
};
/**
 * Waits for the loading state of an observable FetchedData<T> to be Success, then returns the states data.
 * If loading is not successful a generic error is thrown.
 *
 * @param state the observable loading state.
 * @typeparam T The state type.
 */
const getNewStateWhenLoaded = async (state) => {
    await when(() => state.loadingState !== LoadingState.Loading);
    if (state.loadingState !== LoadingState.Success)
        throw new Error('Existing API call did not load data.');
    return getFetchedDataPayload(state);
};
